import { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Store
import { useAppSelector, useAppDispatch } from "app/hooks";
import {
    fetchPaceReport,
    getReport,
    clearReportData,
    getReportStatus,
    TPaceAssessmentReportStatus,
} from "store/slice/Pace";
import { toggleLoader, updateDownloadStatus } from "store/slice/UI";
import { getUserOrgs } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

// Services
import {
    getAssessmentReportService,
    getAssessmentReportServicePDF,
    getAssessmentStatusService,
    getUserService,
} from "services";

// Utils
import { isEmpty } from "lodash";
import { urlPaths } from "enums/urlPaths";
import useResizeObserver from "use-resize-observer";
import { forceDownload, useAuth, useRedirectOnNoPersonId } from "utils";
import usePaceLandingLayout from "../PaceLandingLayout/usePaceLandingLayout";
import { convertReportData } from "store/slice/Pace/report/converter";
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";
import { shallowEqual } from "react-redux";

interface IRefObjectInteractiveChart {
    element: HTMLDivElement | null;
    limit: HTMLDivElement | null;
    result: number | null;
}
interface IRefObjectAnchors {
    navigation: HTMLDivElement[];
    others: HTMLDivElement[];
}

export interface IUserPrivacy {
    isSharingPace: boolean | null;
    totalUserHubs: number | null | undefined;
    hubsNotSharing: number;
    singleHubName: string;
}

export type TPreviousAssessment = {
    id: number | null;
    completedDate: Date | string;
};
const useReportLayout = () => {
    const dispatch = useAppDispatch();
    const ability = useAbility(AbilityContext);

    const reportData = useAppSelector(getReport, shallowEqual);
    const reportStatus = useAppSelector(getReportStatus);
    const userOrganizations = useAppSelector(getUserOrgs);

    const { handleStartPace } = usePaceLandingLayout();

    // Redirect to Dashboard if no personId on Session
    useRedirectOnNoPersonId();

    const { userId, userAssessmentId } = useParams();
    const { hasSession, sessionData, loggedInUserData } = useAuth();

    const selectedOrg = sessionData?.authn.user.org_selected;
    const groupId = selectedOrg?.group_id;

    const isLoggedUser = loggedInUserData.userId === Number(userId);

    const navigate = useNavigate();

    const [currentAnchorId, setCurrentAnchorId] = useState("");
    const [clickedAnchorId, setClickedAnchorId] = useState(false);

    const currentAnchorIndex = useRef(0);
    const anchors = useRef<IRefObjectAnchors>({ navigation: [], others: [] });
    const interactiveChartRef = useRef<IRefObjectInteractiveChart>({ element: null, limit: null, result: null });

    const isNormalUserAndOwnReport =
        (ability.can("see", "SAAS_PACE_REPORT") || ability.can("see", "PACE_REPORT_RUN_OWN")) &&
        `${loggedInUserData.userId}` === userId;

    const isAdminAndOwnOrOthersReport =
        (ability.can("see", "SYS_ADMIN_SCOPE_GRPS_OWN") && `${loggedInUserData.userId}` !== userId) ||
        (ability.can("see", "SYS_ADMIN_SCOPE_GRPS_ALL") && `${loggedInUserData.userId}` !== userId);

    const canSeeThisPage = isNormalUserAndOwnReport || isAdminAndOwnOrOthersReport;

    const { ref, width, height } = useResizeObserver<HTMLDivElement>();

    const [showLegends, setShowLegends] = useState(false);

    const [userPrivacy, setUserPrivacy] = useState<IUserPrivacy>({
        isSharingPace: false,
        totalUserHubs: 0,
        hubsNotSharing: 0,
        singleHubName: "",
    });

    const [canRequestUserData, setCanRequestUserData] = useState(true);

    const [shouldShowBanner, setShouldShowBanner] = useState<boolean>(false);
    const [showPrevPacePopover, setShowPrevPacePopover] = useState<boolean>(false);
    const [prevPacePopoverAnchorEl, setPrevPacePopoverAnchorEl] = useState<HTMLElement | null>(null);
    const selectedPreviousPaceRef = useRef<TPreviousAssessment>({
        id: null,
        completedDate: "",
    });
    const [selectedPreviousPace, setSelectedPreviousPace] = useState<TPreviousAssessment | null>(
        selectedPreviousPaceRef.current
    );

    const [didInitialLoadReport, setDidInitialLoadReport] = useState<boolean>(false);

    const lastCompletedPaceId = reportStatus?.user_assessments?.find(
        (item) => item.meta.status === "FINISHED_COMPLETED"
    )?.user_assessment_id;

    const [externalReportData, setExternalReportData] = useState<ReturnType<typeof convertReportData>>(null);
    const [externalReportStatus, setExternalReportStatus] = useState<TPaceAssessmentReportStatus>(null);

    const [previousAssessmentsList, setPreviousAssessmentsList] = useState<TPreviousAssessment[] | null>(null);
    const [headerPaceDate, setHeaderPaceDate] = useState<Date | null>(null);

    const [shouldShrinkHeader, setShouldShrinkHeader] = useState(false);
    const triggerShrinkHeaderRef = useRef(null);

    const completedOn = isLoggedUser
        ? reportStatus?.user_assessments?.find((item) => item.meta.status === "FINISHED_COMPLETED")?.meta?.completed
        : externalReportStatus?.user_assessments?.find((item) => item.meta.status === "FINISHED_COMPLETED")?.meta
              ?.completed;

    const handleShowPreviousPacePopover = (e: React.MouseEvent<HTMLButtonElement>) => {
        setPrevPacePopoverAnchorEl(e.currentTarget);
        setShowPrevPacePopover(true);
    };

    const onClosePrevPacePopover = () => {
        setPrevPacePopoverAnchorEl(null);
        setShowPrevPacePopover(false);
        setSelectedPreviousPace({ id: null, completedDate: "" });
    };

    const handleRetestPace = async () => {
        handleStartPace();
    };

    const handleScrollTo = useCallback((to, shouldSetClickedAnchorId = false) => {
        const element = [...anchors.current.navigation, ...anchors.current.others]?.find(
            (anchor) => anchor?.getAttribute("id") === to
        );

        if (element?.offsetTop) {
            window.scrollTo({
                top: element.getBoundingClientRect().top + window.scrollY - 90,
                left: 0,
                behavior: "smooth",
            });
        }

        if (shouldSetClickedAnchorId) {
            setClickedAnchorId(true);
            setCurrentAnchorId(to);

            setTimeout(() => {
                setClickedAnchorId(false);
            }, 800);
        }
    }, []);

    const goToNextAnchor = () => {
        const nextIndex = currentAnchorIndex.current + 1;

        if (nextIndex <= anchors.current.others.length - 1) {
            currentAnchorIndex.current = nextIndex;
            setCurrentAnchorId(anchors.current.others[nextIndex].getAttribute("id") || "");
            handleScrollTo(anchors.current.others[nextIndex].getAttribute("id"));
        } else {
            navigate(urlPaths.PaceAssessment);
        }
    };

    const handleAddRef = (element: HTMLParagraphElement) => {
        if (!element || !element?.getAttribute("id")) return;

        const elementId = element?.getAttribute("id");
        const currentAnchorsIds = [...anchors.current.navigation, ...anchors.current.others].map((el) =>
            el?.getAttribute("id")
        );

        if (!currentAnchorsIds.includes(elementId)) {
            element?.getAttribute("class")?.includes("sectionWrapper")
                ? anchors.current.navigation.push(element)
                : anchors.current.others.push(element);
        }
    };

    const handleDownloadReport = async () => {
        try {
            dispatch(updateDownloadStatus({ loading: true, error: false }));

            if (!userId || !userAssessmentId) return;

            const userIdToNumber = parseInt(userId);
            const userAssessmentIdToNumber = parseInt(userAssessmentId);
            const pdfResponse = await getAssessmentReportServicePDF(userIdToNumber, userAssessmentIdToNumber, groupId);
            forceDownload(pdfResponse, "PACE_Report.pdf");

            dispatch(updateDownloadStatus({ loading: false, error: false }));
        } catch (error) {
            console.error(error);
            dispatch(updateDownloadStatus({ loading: false, error: true }));

            setTimeout(() => {
                dispatch(updateDownloadStatus({ loading: false, error: false }));
            }, 5000);
        }
    };

    const handlePreviousReportRequest = () => {
        if (selectedPreviousPace?.id && userId) {
            selectedPreviousPaceRef.current = selectedPreviousPace;
            anchors.current = { navigation: [], others: [] };

            navigate(`/pace/report/user/${userId}/assessment/${selectedPreviousPace.id}`, {
                replace: true,
            });
        }

        onClosePrevPacePopover();
    };

    useEffect(() => {
        if (!canSeeThisPage) navigate("/");
    }, [canSeeThisPage, navigate]);

    // Update selected assessment taking into account the parameters in the URL
    useEffect(() => {
        if (previousAssessmentsList && userAssessmentId && !selectedPreviousPace?.id) {
            const selectedFromUrl = previousAssessmentsList.find((item) => item.id === parseInt(userAssessmentId));

            if (selectedFromUrl) {
                setSelectedPreviousPace(selectedFromUrl);
                selectedPreviousPaceRef.current = selectedFromUrl;
            }
        }
    }, [previousAssessmentsList, userAssessmentId, selectedPreviousPace?.id]);

    // Fetch new report when the user picks a different report from the list
    useEffect(() => {
        if (userAssessmentId) {
            if (reportData?.meta?.user_assessment_id !== parseInt(userAssessmentId)) {
                dispatch(clearReportData());
                setDidInitialLoadReport(false); // This allows the new fetchPaceReport
            }
        }
    }, [userAssessmentId, reportData, dispatch]);

    // Get current user to extract privacy settings
    useEffect(() => {
        const getPrivacySettings = async () => {
            setCanRequestUserData(false);
            try {
                const { data } = await getUserService();
                let notSharing: { org_id: number; org_share_data: boolean }[] = [];
                let totalUserHubs = 0;

                if (Array.isArray(data.org_share_data)) {
                    notSharing = data.org_share_data.filter((hub) => !hub.org_share_data);
                    totalUserHubs = data.org_share_data.length;
                }

                setUserPrivacy({
                    isSharingPace: data.optin_share_data,
                    totalUserHubs: totalUserHubs,
                    hubsNotSharing: notSharing.length,
                    singleHubName:
                        notSharing.length === 1
                            ? userOrganizations.data.find((org) => org.id === notSharing[0].org_id)?.name ?? ""
                            : "",
                });
                setShouldShowBanner(!data.optin_share_data || notSharing.length >= 1);
            } catch (error) {
                console.error(error);
            }
        };
        if (canRequestUserData) {
            getPrivacySettings();
        }
    }, [canRequestUserData, userOrganizations.data]);

    // Update assessments list
    useEffect(() => {
        if (!isLoggedUser || !reportStatus?.user_assessments) {
            setPreviousAssessmentsList(null);
            return;
        }

        const list = reportStatus.user_assessments
            .filter((item) => item.meta.status === "FINISHED_COMPLETED")
            .map((item) => ({
                id: item.user_assessment_id,
                completedDate: item.meta.completed,
            }));

        setPreviousAssessmentsList(list);
    }, [isLoggedUser, reportStatus]);

    // CLear previous selected report when userId changes and update header date
    useEffect(() => {
        setSelectedPreviousPace(null);
    }, [userId]);

    useEffect(() => {
        const dateStr = selectedPreviousPace?.completedDate ?? completedOn;

        const date = dateStr ? new Date(dateStr) : null;
        setHeaderPaceDate(date && !isNaN(date.getTime()) ? date : null);
    }, [selectedPreviousPace?.completedDate, completedOn, userId, reportStatus]);

    // Fetch Assessment Report
    useEffect(() => {
        const getReport = async (userId: string, userAssessmentId: string) => {
            dispatch(toggleLoader(true));
            if (isLoggedUser) {
                await dispatch(
                    fetchPaceReport({ userId: parseInt(userId), userAssessmentId: parseInt(userAssessmentId), groupId })
                );
            } else {
                try {
                    const [reportResponse, statusResponse] = await Promise.all([
                        getAssessmentReportService(parseInt(userId), parseInt(userAssessmentId), groupId),
                        getAssessmentStatusService(parseInt(userId)),
                    ]);

                    const convertedReport = convertReportData(reportResponse.data);
                    setExternalReportData(convertedReport);
                    setExternalReportStatus(statusResponse.data);
                    const completedAssessments =
                        statusResponse.data &&
                        statusResponse.data.user_assessments
                            .filter((item) => item.meta.status === "FINISHED_COMPLETED")
                            .map((item) => ({
                                id: item.user_assessment_id,
                                completedDate: item.meta.completed,
                            }));

                    setPreviousAssessmentsList(completedAssessments);
                } catch (error) {
                    console.error(error);
                    navigate(urlPaths.NotFound);
                }
            }

            setDidInitialLoadReport(true);
            dispatch(toggleLoader(false));
        };

        if (
            isEmpty(reportData) &&
            hasSession &&
            userId &&
            userAssessmentId &&
            canSeeThisPage &&
            !didInitialLoadReport
        ) {
            getReport(userId, userAssessmentId);
        } else {
            if (
                userId &&
                reportData?.meta?.user_id !== parseInt(userId) &&
                userAssessmentId &&
                reportData?.meta?.user_assessment_id !== parseInt(userAssessmentId)
            ) {
                dispatch(clearReportData());
            }
        }
    }, [
        dispatch,
        reportData,
        userAssessmentId,
        userId,
        hasSession,
        groupId,
        canSeeThisPage,
        didInitialLoadReport,
        isLoggedUser,
        reportStatus,
        navigate,
    ]);

    // Calculate interactive chart (div) wrapper's height
    useLayoutEffect(() => {
        if (interactiveChartRef.current.element && interactiveChartRef.current.limit) {
            interactiveChartRef.current.result =
                interactiveChartRef.current?.limit?.getBoundingClientRect().top -
                interactiveChartRef.current?.element?.getBoundingClientRect().top -
                60;
        }
    }, [interactiveChartRef.current.element, interactiveChartRef.current.limit, width, height]);

    // Observer to shrink header
    useEffect(() => {
        const trigger = triggerShrinkHeaderRef.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                setShouldShrinkHeader(!entry.isIntersecting);
            },
            {
                rootMargin: "0px",
                threshold: 0,
            }
        );

        if (!isEmpty(reportData || externalReportData) && trigger) {
            observer.observe(trigger);
        }

        return () => {
            if (trigger) {
                observer.unobserve(trigger);
            }
        };
    }, [reportData, externalReportData]);

    // Observer to control anchors
    useEffect(() => {
        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries;

            if (!entry?.isIntersecting || !entry?.target?.id || clickedAnchorId) return;

            const index = anchors.current.others.findIndex((el) => el.getAttribute("id") === entry?.target?.id);
            currentAnchorIndex.current = index > -1 ? index : currentAnchorIndex.current;

            setCurrentAnchorId(entry?.target?.id);
        };

        const observer = new IntersectionObserver(handleIntersection, {
            rootMargin: "-25% 0px -75% 0px",
            threshold: 0,
        });

        if ([...anchors.current.navigation, ...anchors.current.others].length) {
            [...anchors.current.navigation, ...anchors.current.others].forEach((el) => observer.observe(el));
        }

        return () => observer.disconnect();
    }, [currentAnchorId, reportData, clickedAnchorId]);

    return {
        ref,
        reportData: isLoggedUser ? reportData : externalReportData,
        reportStatus: isLoggedUser ? reportStatus : externalReportStatus,
        userPrivacy,
        currentAnchorId,
        interactiveChartRef,
        showLegends,
        shouldShowBanner,
        setShouldShowBanner,
        setShowLegends,
        showPrevPacePopover,
        prevPacePopoverAnchorEl,
        selectedPreviousPace,
        setSelectedPreviousPace,
        selectedPreviousPaceRef,
        headerPaceDate,
        previousAssessmentsList,
        completedOn,
        lastCompletedPaceId,
        isLoggedUser,
        handleAddRef,
        handleScrollTo,
        goToNextAnchor,
        handleDownloadReport,
        handleShowPreviousPacePopover,
        handlePreviousReportRequest,
        onClosePrevPacePopover,
        handleRetestPace,
        shouldShrinkHeader,
        triggerShrinkHeaderRef,
    };
};

export default useReportLayout;
